import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-white">
      <Navbar />

      {loading ? (
        <div className="max-w-3xl mx-auto px-6 pt-36 pb-16">
          <div className="shimmer h-8 rounded w-1/2 mb-4" />
          <div className="shimmer h-12 rounded w-full mb-6" />
          <div className="shimmer h-64 rounded mb-6" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="shimmer h-4 rounded" />
            ))}
          </div>
        </div>
      ) : notFound ? (
        <div className="max-w-3xl mx-auto px-6 pt-36 pb-16 text-center">
          <h2 className="font-heading font-bold text-primary text-3xl mb-4">Post no encontrado</h2>
          <Link to="/blog" className="text-accent hover:underline font-body">Volver al blog</Link>
        </div>
      ) : post ? (
        <>
          {/* Hero */}
          <div
            className="relative h-72 md:h-96 overflow-hidden mt-16"
            style={{
              backgroundImage: `url(${post.image_url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-[#003366]/75" />
            <div className="relative h-full flex flex-col justify-end max-w-3xl mx-auto px-6 pb-10">
              <span className="bg-accent text-white text-xs font-heading font-bold px-3 py-1.5 rounded-full inline-block mb-4 w-fit">
                {post.category}
              </span>
              <h1 className="font-heading font-black text-white text-3xl md:text-5xl leading-tight">
                {post.title}
              </h1>
            </div>
          </div>

          {/* Content */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto px-6 py-12"
          >
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-5 text-gray-400 text-sm font-body mb-8 pb-8 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <User size={14} />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={14} />
                <span>{post.published_at ? new Date(post.published_at).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" }) : ""}</span>
              </div>
              <div className="flex items-center gap-2">
                <Tag size={14} />
                <span>{post.category}</span>
              </div>
            </div>

            {/* Excerpt */}
            <p className="font-body text-lg text-gray-600 leading-relaxed mb-8 font-medium border-l-4 border-accent pl-5">
              {post.excerpt}
            </p>

            {/* Main content */}
            <div className="blog-content font-body text-gray-700">
              {post.content?.split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Back */}
            <div className="mt-12 pt-8 border-t border-gray-100 flex items-center justify-between">
              <Link
                to="/blog"
                data-testid="back-to-blog"
                className="flex items-center gap-2 text-primary hover:text-accent font-heading font-semibold text-sm transition-colors duration-200"
              >
                <ArrowLeft size={16} /> Volver al Blog
              </Link>
              <a
                href="#contacto"
                onClick={(e) => { e.preventDefault(); window.location.href = "/#contacto"; }}
                className="bg-accent text-white px-6 py-2.5 rounded-full font-heading font-bold text-sm hover:bg-[#0099D6] transition-colors duration-300"
              >
                Contactanos
              </a>
            </div>
          </motion.article>
        </>
      ) : null}

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
